<script lang="ts">
	import newsPlaceholder from "@core/assets/images/news-placeholder.svg";
	import type { Product } from "@core/schema/Product.js";
	import type { HoverZoomOptions } from "@core/utils/hoverZoom.js";
	import { makeInflect } from "@core/utils/makeInflect.js";
	import Lightbox from "./common/Lightbox.svelte";
	import Media from "./common/Media.svelte";

	const { product }: { product: Product } = $props();
	let currentIndex = $state(0);

	const shownGalleryItem = $derived(product.gallery[currentIndex]);

	const zoomOptions: HoverZoomOptions = {
		enabled: true,
		zoomLevel: 2,
	};
	const makeMoreLabel = makeInflect("další", "další", "dalších");

	const MAX_VISIBLE = 5;
	const BASE_THUMBS = 4;

	const totalImages = $derived(product.gallery.length);
	const hasOverflow = $derived(totalImages > MAX_VISIBLE);

	const visibleThumbnails = $derived(
		totalImages <= 1 ? [] : !hasOverflow ? product.gallery.slice(1) : product.gallery.slice(1, 1 + BASE_THUMBS),
	);

	const remainingCount = $derived(hasOverflow ? Math.max(totalImages - MAX_VISIBLE, 0) : 0);

	/* eslint-disable @typescript-eslint/explicit-function-return-type */
</script>

<div class="bg-white/50 p-4 backdrop-blur-[8px] w-[42rem] max-w-full rounded-3xl">
	<div class="relative size-full">
		<button
			class="border-light aspect-[4/3] size-full overflow-hidden rounded-lg min-w-"
			onclick={() => {
				window.fsLightboxInstances[product.id]?.open(currentIndex);
			}}
		>
			{#if shownGalleryItem?.youtube}
				<Media
					alt="Banner"
					wrapperClass="size-full"
					height={362}
					source={{ youtube: shownGalleryItem.youtube }}
					class="size-full object-cover"
					width={476}
					lightbox={product.id}
					sourcesets={{
						"<2xl": [0, 476],
						">2xl": [0, 476],
					}}
				/>
			{:else if shownGalleryItem?.photo}
				<Media
					alt={shownGalleryItem.photo.title ?? ""}
					wrapperClass="size-full"
					height={362 * zoomOptions.zoomLevel}
					source={shownGalleryItem.photo}
					class="size-full object-cover"
					width={476 * zoomOptions.zoomLevel}
					lightbox={product.id}
					sourcesets={{
						"<2xl": [0, 476 * 2],
						">2xl": [0, 476 * 2],
					}}
				/>
			{:else}
				<Media
					alt=""
					wrapperClass="size-full"
					height={362 * zoomOptions.zoomLevel}
					source={{
						src: newsPlaceholder,
						type: "image/svg+xml",
					}}
					class="size-full object-cover"
					width={476 * zoomOptions.zoomLevel}
					lightbox={product.id}
					sourcesets={{
						"<2xl": [0, 476 * 2],
						">2xl": [0, 476 * 2],
					}}
				/>
			{/if}
		</button>
	</div>

	{#if product.gallery.length > 1}
		<div class="flex items-center gap-2">
			{#each visibleThumbnails as galleryItem, index (galleryItem.id)}
				<button
					type="button"
					class="relative h-20 w-32 overflow-hidden rounded-lg"
					onmouseenter={() => {
						currentIndex = index + 1;
					}}
					onmouseleave={() => {
						currentIndex = 0;
					}}
				>
					{#if galleryItem.youtube}
						<Media
							alt="YouTube video"
							wrapperClass="size-full"
							class="size-full object-cover"
							height={64}
							width={96}
							source={{ youtube: galleryItem.youtube }}
							lightbox={product.id}
							sourcesets={{
								"<2xl": [0, 96],
								">2xl": [0, 96],
							}}
						/>
					{:else if galleryItem.photo}
						<Media
							alt={galleryItem.photo.title ?? ""}
							wrapperClass="size-full"
							class="size-full object-cover"
							height={64}
							width={96}
							source={galleryItem.photo}
							lightboxWidth={1920}
							lightbox={product.id}
							sourcesets={{
								"<2xl": [0, 96],
								">2xl": [0, 96],
							}}
						/>
					{:else}
						<Media
							alt=""
							wrapperClass="size-full"
							class="size-full object-cover"
							height={64}
							width={96}
							source={{
								src: newsPlaceholder,
								type: "image/svg+xml",
							}}
							lightbox={product.id}
							sourcesets={{
								"<2xl": [0, 96],
								">2xl": [0, 96],
							}}
						/>
					{/if}
				</button>
			{/each}

			{#if remainingCount > 0}
				<button
					type="button"
					class="flex h-20 w-32 flex-col items-end justify-center rounded-lg text-right bg-cmcsecondary font-bold text-white leading-tight p-1 sm:p-4"
					onclick={() => {
						window.fsLightboxInstances[product.id]?.open(remainingCount + 1);
					}}
				>
					+{remainingCount}<br />
					<span class="font-normal">{makeMoreLabel(remainingCount)}</span>
				</button>
				<div class="hidden" aria-hidden="true">
					{#each product.gallery.slice(remainingCount + 1) as galleryItem (galleryItem.id)}
						{#if galleryItem.photo}
							<Lightbox id={product.id} source={{ width: 1920, media: galleryItem.photo }} type="image" />
						{:else if galleryItem.youtube}
							<Lightbox id={product.id} source={{ href: galleryItem.youtube }} type="youtube" />
						{/if}
					{/each}
				</div>
			{/if}
		</div>
	{/if}
</div>
